import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserCertificateService, CertificateData } from '../../services/user-certificate.service';
import { environment } from '../../../environments/environment';
import { catchError, timeout, finalize, tap } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';

interface CpdHourRecord {
  id: string;
  hoursStudied: string;
  studyDate: string;
  enrolledAt: string;
  completedAt: string | null;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  isCompleted?: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  courseId: string;
  userCourseId: string | null;
  cpdCourseStatusId?: number;
  cpdPaymentStatusId?: number;
  typeReceipt?: string | null;
  image?: string | null;          // หลักฐาน/ใบประกาศที่แนบตอนลงทะเบียน (รูปเต็ม)
  imageThumbnail?: string | null; // รูปย่อของหลักฐาน
  user: {
    id: string;
    email: string;
    cid: string;
    isActive: boolean;
  };
  course: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    thumbnail: string | null;
    price: string;
    isActive: boolean;
    certificationBySEC?: boolean;
    startDate: string;
    endDate: string;
    createdAt: string;
    updatedAt: string;
  };
  userCourse: any;
  cpdCourseStatus?: {
    id: number;
    name: string;
  };
  cpdPaymentStatus?: {
    id: number;
    name: string;
  };
}

@Component({
  selector: 'app-user-course-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-course-list.component.html',
  styleUrl: './user-course-list.component.scss'
})
export class UserCourseListComponent implements OnInit {
  cpdRecords: CpdHourRecord[] = [];
  allCpdRecords: CpdHourRecord[] = []; // Store all records before filtering
  isLoading = false; // Never show loading state
  error: string | null = null;
  userId: string | null = null;
  filterMode: 'all' | '3years' = 'all'; // Filter parameter from URL

  // Summary statistics
  totalCpdHours = 0;
  verifiedHours = 0;
  unverifiedHours = 0;
  secCertifiedHours = 0; // SEC-certified CPD hours
  totalCourses = 0;
  currentYear = new Date().getFullYear();

  // Payment slip upload
  paymentSlips: { [recordId: string]: File | null } = {};
  paymentSlipPreviews: { [recordId: string]: string | null } = {};
  uploadingSlips: { [recordId: string]: boolean } = {};
  // Transfer date-time reported by the user (formatted Thai text, per record)
  transferDateTimes: { [recordId: string]: string } = {};
  // Receipt type per record ('individual' | 'company')
  receiptTypes: { [recordId: string]: string } = {};

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private certificateService: UserCertificateService
  ) {
    console.log('UserCourseListComponent constructor called');
  }

  ngOnInit(): void {
    console.log('UserCourseListComponent initialized');

    // Get filter parameter from URL query params
    this.route.queryParams.subscribe(params => {
      const filter = params['filter'];
      if (filter === '3years') {
        this.filterMode = '3years';
        console.log('📅 Filter mode set to: 3years');
      } else {
        this.filterMode = 'all';
        console.log('📅 Filter mode set to: all');
      }
      this.loadUserCourses();
    });
  }

  loadUserCourses(): void {
    console.log('Starting to load user courses...');
    // Don't set isLoading to true - we want to show content immediately
    this.error = null;

    // Get user ID from auth service
    const currentUser = this.authService.getCurrentUser();
    console.log('Current user from AuthService:', currentUser);

    if (!currentUser || !currentUser.id) {
      console.error('No user found or user has no ID');
      this.error = 'ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบอีกครั้ง';
      setTimeout(() => this.router.navigate(['/login']), 2000);
      return;
    }

    this.userId = currentUser.id;
    const apiUrl = `${environment.apiUrl}/cpd-hours/user/${this.userId}`;
    console.log('Fetching CPD hours from URL:', apiUrl);
    console.log('Environment API URL:', environment.apiUrl);

    // Fetch CPD hours data from API
    this.http.get<CpdHourRecord[]>(apiUrl).pipe(
      tap(response => {
        console.log('🔍 Raw HTTP response received:', response);
        console.log('🔍 Response type:', typeof response);
        console.log('🔍 Is Array:', Array.isArray(response));
      }),
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ HTTP Error occurred:', error);
        console.error('Error status:', error.status);
        console.error('Error statusText:', error.statusText);
        console.error('Error message:', error.message);
        console.error('Error error:', error.error);

        if (error.status === 0) {
          this.error = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend API กำลังทำงานที่ ' + environment.apiUrl;
        } else if (error.status === 404) {
          console.log('No CPD records found for user, showing empty state');
          this.error = null; // Don't show error for empty data
        } else if (error.status === 401 || error.status === 403) {
          this.error = 'ไม่มีสิทธิ์เข้าถึงข้อมูล กรุณาเข้าสู่ระบบใหม่';
          setTimeout(() => this.router.navigate(['/login']), 2000);
        } else {
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล (HTTP ${error.status}: ${error.statusText})`;
        }

        this.cdr.detectChanges(); // Force change detection on error
        return of([]);
      }),
      finalize(() => {
        console.log('🏁 API call completed');
        this.cdr.detectChanges(); // Force change detection when complete
      })
    ).subscribe({
      next: (data) => {
        console.log('✅ CPD hours data received in subscribe');
        console.log('📊 Number of records:', data?.length ?? 0);
        console.log('📦 Data:', data);
        console.log('📦 Data stringified:', JSON.stringify(data, null, 2));

        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.warn('⚠️ Data is not an array, converting...');
          this.allCpdRecords = [];
          this.cpdRecords = [];
        } else {
          // Sort by most recently updated first so the newest activity is
          // always at the top for easier use.
          this.allCpdRecords = this.sortByLatestUpdate([...data]); // Store all records
          console.log('✅ All CPD records loaded:', this.allCpdRecords.length, 'records');

          // Initialize receiptTypes with existing values from the database
          this.allCpdRecords.forEach(record => {
            if (record.typeReceipt) {
              this.receiptTypes[record.id] = record.typeReceipt;
            }
          });

          // Apply filter based on filterMode
          this.applyFilter();
        }

        this.calculateStatistics();

        console.log('📈 Statistics calculated:');
        console.log('  - Total CPD Hours:', this.totalCpdHours);
        console.log('  - Verified Hours:', this.verifiedHours);
        console.log('  - Unverified Hours:', this.unverifiedHours);
        console.log('  - Total Courses:', this.totalCourses);
        console.log('  - cpdRecords.length:', this.cpdRecords.length);

        // Force Angular to detect changes
        this.cdr.detectChanges();
        console.log('🔄 Change detection triggered');
      },
      error: (error) => {
        console.error('❌ Unexpected error in subscribe:', error);
        this.error = 'เกิดข้อผิดพลาดที่ไม่คาดคิด: ' + error.message;
        this.cdr.detectChanges();
      },
      complete: () => {
        console.log('✅ Observable completed');
      }
    });
  }

  /**
   * Sort records so the most recently updated appear first.
   * Falls back to createdAt/enrolledAt when updatedAt is missing.
   */
  private sortByLatestUpdate(records: CpdHourRecord[]): CpdHourRecord[] {
    const getTimestamp = (record: CpdHourRecord): number => {
      const dateStr = record.updatedAt || record.createdAt || record.enrolledAt;
      const time = dateStr ? new Date(dateStr).getTime() : NaN;
      return isNaN(time) ? 0 : time;
    };

    return records.sort((a, b) => getTimestamp(b) - getTimestamp(a));
  }

  /**
   * Apply filter based on filterMode
   * - 'all': Show all records
   * - '3years': Show only records from the last 3 years (based on enrolledAt)
   */
  private applyFilter(): void {
    if (this.filterMode === '3years') {
      // Calculate date 3 years ago from now
      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
      console.log('📅 Filtering CPD hours from:', threeYearsAgo.toISOString());

      // Filter records from the last 3 years (based on enrolledAt)
      this.cpdRecords = this.allCpdRecords.filter(record => {
        if (!record.enrolledAt) {
          console.warn('⚠️ Record missing enrolledAt:', record.id);
          return false;
        }
        const enrolledDate = new Date(record.enrolledAt);
        const isWithin3Years = enrolledDate >= threeYearsAgo;

        if (isWithin3Years) {
          console.log('✅ Including record (3years filter):', {
            id: record.id,
            hoursStudied: record.hoursStudied,
            enrolledAt: record.enrolledAt
          });
        }

        return isWithin3Years;
      });

      console.log('✅ Filtered records from last 3 years (by enrolledAt):', this.cpdRecords.length);
    } else {
      // Show all records
      this.cpdRecords = [...this.allCpdRecords];
      console.log('✅ Showing all records:', this.cpdRecords.length);
    }
  }

  calculateStatistics(): void {
    this.totalCpdHours = 0;
    this.verifiedHours = 0;
    this.unverifiedHours = 0;
    this.secCertifiedHours = 0;

    const uniqueCourseIds = new Set<string>();

    // Set cutoff date to exactly 3 years ago from today
    const cutoffDate = new Date();
    cutoffDate.setFullYear(cutoffDate.getFullYear() - 3);

    console.log('📅 Calculating statistics with 3-year cutoff from:', cutoffDate.toISOString());

    this.cpdRecords.forEach(record => {
      const hours = parseFloat(record.hoursStudied) || 0;
      const studyDate = new Date(record.studyDate);
      const isWithin3Years = studyDate >= cutoffDate;

      if (isWithin3Years) {
        this.totalCpdHours += hours;

        if (record.isVerified) {
          this.verifiedHours += hours;
        } else {
          this.unverifiedHours += hours;
        }

        // Sum SEC-certified hours ONLY if:
        // 1. certificationBySEC is true
        // 2. isVerified is true
        // 3. isCompleted is true
        const certificationBySEC = record.course?.certificationBySEC === true;
        const isVerified = record.isVerified === true;
        const isCompleted = record.isCompleted === true || (record as any).isCompleted === true;

        if (certificationBySEC && isVerified && isCompleted) {
          this.secCertifiedHours += hours;
          console.log('✅ SEC-certified record counted:', {
            id: record.id,
            hours: hours,
            certificationBySEC,
            isVerified,
            isCompleted,
            studyDate: record.studyDate
          });
        }

        console.log('✅ Record counted (within 3 years):', {
          id: record.id,
          hours: hours,
          studyDate: record.studyDate,
          isVerified: record.isVerified
        });
      } else {
        console.log('❌ Record excluded (older than 3 years):', {
          id: record.id,
          hours: hours,
          studyDate: record.studyDate
        });
      }

      uniqueCourseIds.add(record.courseId);
    });

    console.log('📊 Final statistics (3-year backward count):');
    console.log('  - Total CPD Hours:', this.totalCpdHours);
    console.log('  - Verified Hours:', this.verifiedHours);
    console.log('  - Unverified Hours:', this.unverifiedHours);
    console.log('  - SEC-certified hours:', this.secCertifiedHours);
    console.log('  - Total Courses:', uniqueCourseIds.size);
    
    this.totalCourses = uniqueCourseIds.size;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      // Course dates are naive wall-clock tagged UTC; format in UTC to avoid
      // the +7h (ICT) shift that rolls evening times to the next calendar day.
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatPrice(price: string): string {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'beginner':
        return 'badge-success';
      case 'intermediate':
        return 'badge-warning';
      case 'advanced':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  getLevelText(level: string): string {
    switch (level) {
      case 'beginner':
        return 'เบื้องต้น';
      case 'intermediate':
        return 'ปานกลาง';
      case 'advanced':
        return 'ขั้นสูง';
      default:
        return level;
    }
  }

  getCategoryBadgeClass(category: string): string {
    const categoryMap: { [key: string]: string } = {
      'Accounting': 'badge-primary',
      'Tax': 'badge-info',
      'Standards': 'badge-warning',
      'Ethics': 'badge-success',
      'Cost Accounting': 'badge-secondary'
    };
    return categoryMap[category] || 'badge-secondary';
  }

  goBack(): void {
    this.router.navigate(['/user/dashboard']);
  }

  retry(): void {
    console.log('Retrying to load courses...');
    this.loadUserCourses();
  }

  getCpdCourseStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ยืนยันแล้ว':
      case 'อนุมัติ':
      case 'Approved':
        return 'badge-success';
      case 'รอยืนยัน':
      case 'รอการอนุมัติ':
      case 'รอ Approve':
        return 'badge-warning';
      case 'ไม่อนุมัติ':
      case 'ยกเลิก':
      case 'ยกเลิกใบคำขอ':
        return 'badge-danger';
      case 'คำขอเก่า':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  }

  getCpdPaymentStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระเงินแล้ว':
      case 'ชำระแล้ว':
        return 'badge-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'badge-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
        return 'badge-info';
      case 'ยกเลิกการชำระ':
      case 'ยกเลิก':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  /**
   * Handle payment slip file selection
   */
  onPaymentSlipSelected(event: Event, recordId: string): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg'];

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert('กรุณาอัปโหลดไฟล์ JPG หรือ JPEG เท่านั้น');
      input.value = '';
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
      input.value = '';
      return;
    }

    // Store file
    this.paymentSlips[recordId] = file;

    // Create preview
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.paymentSlipPreviews[recordId] = e.target?.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);

    console.log('Payment slip selected for record:', recordId, file.name);
  }

  /**
   * Remove payment slip
   */
  removePaymentSlip(recordId: string): void {
    delete this.paymentSlips[recordId];
    delete this.paymentSlipPreviews[recordId];
    delete this.transferDateTimes[recordId];
    this.cdr.detectChanges();
  }

  /**
   * Get selected receipt type for a record
   */
  getReceiptType(recordId: string): string {
    return this.receiptTypes[recordId] || '';
  }

  /**
   * Check if receipt type is already set from database (read-only)
   */
  isReceiptTypeReadOnly(recordId: string): boolean {
    const record = this.allCpdRecords.find(r => r.id === recordId);
    return !!(record && record.typeReceipt);
  }

  /**
   * Get receipt type label for display
   */
  getReceiptTypeLabel(type: string): string {
    if (type === 'individual') {
      return 'ออกใบเสร็จแบบบุคคลธรรมดา';
    } else if (type === 'company') {
      return 'ออกใบเสร็จแบบนิติบุคคล';
    }
    return '';
  }

  /**
   * Handle receipt type change and persist to database (mirrors exam flow)
   */
  onReceiptTypeChange(recordId: string, type: string): void {
    // Prevent change if already set from database
    if (this.isReceiptTypeReadOnly(recordId)) {
      console.log('Receipt type is read-only, cannot change:', recordId);
      return;
    }

    this.receiptTypes[recordId] = type;
    console.log('Receipt type selected for record:', recordId, type);

    this.updateReceiptType(recordId, type);
    this.cdr.detectChanges();
  }

  /**
   * Update receipt type (typeReceipt) in the database
   */
  updateReceiptType(recordId: string, type: string): void {
    const apiUrl = `${environment.apiUrl}/cpd-hours/${recordId}`;
    const payload = { typeReceipt: type };

    console.log('Updating receipt type in database:', recordId, type);

    this.http.patch(apiUrl, payload).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error updating receipt type:', error);
        alert('เกิดข้อผิดพลาดในการบันทึกประเภทใบเสร็จ กรุณาลองใหม่อีกครั้ง');
        return of(null);
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          console.log('Receipt type updated successfully:', response);
        }
      }
    });
  }

  /**
   * Handle transfer date-time change (mirrors exam registration flow)
   */
  onTransferDateTimeChange(recordId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const dateTimeValue = input.value;

    if (dateTimeValue) {
      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to Thai readable format
      const dateTime = new Date(dateTimeValue);
      const formattedDateTime = this.formatTransferDateTime(dateTime);

      this.transferDateTimes[recordId] = formattedDateTime;
      console.log('Transfer date-time selected for record:', recordId, formattedDateTime);
      this.cdr.detectChanges();
    } else {
      delete this.transferDateTimes[recordId];
      this.cdr.detectChanges();
    }
  }

  /**
   * Format date-time to Thai readable text format (e.g. "20 เมษายน 2567 เวลา 14:30 น.")
   */
  formatTransferDateTime(date: Date): string {
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const day = date.getDate();
    const month = thaiMonths[date.getMonth()];
    const year = date.getFullYear() + 543; // Convert to Buddhist year
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} ${year} เวลา ${hours}:${minutes} น.`;
  }

  /**
   * Get formatted transfer date-time for a record
   */
  getTransferDateTime(recordId: string): string {
    return this.transferDateTimes[recordId] || '';
  }

  /**
   * Upload payment slip to backend
   */
  uploadPaymentSlip(recordId: string): void {
    const file = this.paymentSlips[recordId];
    if (!file) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    // Validate receipt type is selected (บุคคลธรรมดา / นิติบุคคล)
    if (!this.receiptTypes[recordId]) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อน (บุคคลธรรมดา / นิติบุคคล)');
      return;
    }

    // Validate transfer date-time is provided (mirrors exam registration flow)
    const transferDateTime = this.transferDateTimes[recordId];
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาการโอนเงินก่อน');
      return;
    }

    // Set uploading state
    this.uploadingSlips[recordId] = true;

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);  // Changed from 'paymentSlip' to 'file'm[]
    formData.append('transferDateTime', transferDateTime);

    // API endpoint for uploading payment slip
    const uploadUrl = `${environment.apiUrl}/cpd-hours/${recordId}/upload-payment-slip`;

    console.log('Uploading payment slip to:', uploadUrl);
    console.log('File name:', file.name);
    console.log('File size:', file.size);

    this.http.post(uploadUrl, formData).pipe(
      timeout(30000),
      catchError((error: HttpErrorResponse) => {
        console.error('Error uploading payment slip:', error);
        this.uploadingSlips[recordId] = false;

        if (error.status === 0) {
          alert('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง');
        } else {
          alert(`เกิดข้อผิดพลาดในการอัปโหลดสลิป: ${error.error?.message || error.message}`);
        }

        return of(null);
      })
    ).subscribe({
      next: (uploadResponse) => {
        if (uploadResponse) {
          console.log('Payment slip uploaded successfully:', uploadResponse);

          // Update payment status to 2 (รอตรวจสอบ) and persist transfer date-time
          this.updatePaymentStatus(recordId, transferDateTime);
        }
      },
      error: (error) => {
        console.error('Unexpected error:', error);
        alert('เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองใหม่อีกครั้ง');
        this.uploadingSlips[recordId] = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update payment status after successful upload
   */
  private updatePaymentStatus(recordId: string, transferDateTime?: string): void {
    const patchUrl = `${environment.apiUrl}/cpd-hours/${recordId}`;
    const payload: { cpdPaymentStatusId: number; transferDateTime?: string } = {
      cpdPaymentStatusId: 2
    };
    if (transferDateTime) {
      payload.transferDateTime = transferDateTime;
    }

    console.log('🔄 Updating payment status to 2 for record:', recordId);
    console.log('🔄 PATCH URL:', patchUrl);
    console.log('🔄 Payload:', payload);

    this.http.patch(patchUrl, payload).pipe(
      timeout(30000),
      tap(response => {
        console.log('✅ PATCH response received:', response);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Error updating payment status:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);
        alert('อัปโหลดสลิปสำเร็จ แต่ไม่สามารถอัปเดตสถานะได้ กรุณาติดต่อเจ้าหน้าที่');
        this.uploadingSlips[recordId] = false;
        this.cdr.detectChanges();
        return of(null);
      }),
      finalize(() => {
        console.log('🏁 PATCH request completed');
        this.uploadingSlips[recordId] = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (patchResponse) => {
        console.log('📥 PATCH subscribe next called with response:', patchResponse);

        // Always execute success actions, even if response is null/empty
        console.log('✅ Payment status updated successfully');
        alert('อัปโหลดสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');

        // Clear the uploaded file
        this.removePaymentSlip(recordId);

        // Reload courses to get updated payment status
        this.loadUserCourses();
      },
      error: (error) => {
        console.error('❌ Unexpected error in PATCH subscribe:', error);
        alert('อัปโหลดสลิปสำเร็จ แต่ไม่สามารถอัปเดตสถานะได้ กรุณาติดต่อเจ้าหน้าที่');
      }
    });
  }

  /**
   * Check if payment slip is uploaded for a record
   */
  hasPaymentSlip(recordId: string): boolean {
    return !!this.paymentSlips[recordId];
  }

  /**
   * Check if payment slip is being uploaded
   */
  isUploadingSlip(recordId: string): boolean {
    return !!this.uploadingSlips[recordId];
  }

  /**
   * Check if study date is from current year or later
   */
  isStudyDateCurrentYear(studyDate: string): boolean {
    const studyYear = new Date(studyDate).getFullYear();
    const currentYear = new Date().getFullYear();
    return studyYear >= currentYear;
  }

  /**
   * Check if study date is from 2026 or later
   * Used to determine if receipt and certificate download buttons should be shown
   */
  isStudyDateFrom2026(studyDate: string): boolean {
    const studyYear = new Date(studyDate).getFullYear();
    return studyYear >= 2026;
  }

  /**
   * คอร์สใหม่หรือไม่ — ใช้ตัดสินว่าโหลดใบเสร็จ/E-Certificate ได้หรือไม่
   * เกณฑ์: วันที่อบรมตั้งแต่ 1 สิงหาคม 2569 (2026-08-01) เป็นต้นไป = คอร์สใหม่
   * คอร์สก่อนปี 2569 และคอร์สปี 2569 เดือน ม.ค.-ก.ค. = คอร์สเก่า (โหลดได้เฉพาะใบประกาศที่แนบ)
   * ใช้วันที่เริ่มอบรมจริงจาก course ก่อน ถ้าไม่มีจึง fallback เป็น studyDate
   */
  isNewCourseForEcert(record: CpdHourRecord): boolean {
    const dateStr = record.course?.startDate || record.studyDate;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const cutoff = new Date(2026, 7, 1); // 1 สิงหาคม 2026 (index เดือน 7 = ส.ค.)
    return d >= cutoff;
  }

  /** มีหลักฐาน/ใบประกาศที่แนบไว้หรือไม่ */
  hasEvidence(record: CpdHourRecord): boolean {
    return !!(record.image || record.imageThumbnail);
  }

  /** ดาวน์โหลด/เปิดใบประกาศแบบกระดาษที่แนบตอนลงทะเบียน (คอร์สเก่า) */
  downloadEvidence(record: CpdHourRecord): void {
    const url = record.image || record.imageThumbnail;
    if (!url) {
      alert('ไม่มีหลักฐานใบประกาศที่แนบไว้ในระบบ');
      return;
    }
    window.open(url, '_blank');
  }

  /**
   * Check if a course has expired (more than 3 years from endDate)
   */
  isCourseExpired(endDate: string): boolean {
    if (!endDate) return false;

    const courseEndDate = new Date(endDate);
    const now = new Date();
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(now.getFullYear() - 3);

    // Course is expired if endDate is more than 3 years ago
    return courseEndDate < threeYearsAgo;
  }

  /**
   * Get expiry status text
   */
  getExpiryStatusText(endDate: string): string {
    return this.isCourseExpired(endDate) ? 'หมดอายุ' : 'ยังใช้ได้';
  }

  /**
   * Get expiry status badge class
   */
  getExpiryStatusBadgeClass(endDate: string): string {
    return this.isCourseExpired(endDate) ? 'badge bg-danger' : 'badge bg-success';
  }

  /**
   * Download receipt for a CPD hour record
   */
  downloadReceipt(recordId: string): void {
    console.log('📄 Navigating to receipt page for record:', recordId);
    const receiptUrl = `/user/renew-slip/${recordId}?type=training`;
    this.router.navigateByUrl(receiptUrl);
  }

  /**
   * Download certificate for a CPD hour record
   */
  async downloadCertificate(record: CpdHourRecord): Promise<void> {
    try {
      console.log('📥 Downloading certificate for record:', record.id);

      // Check if course is completed
      if (!record.isCompleted) {
        alert('⚠️ ไม่สามารถดาวน์โหลดใบประกาศนียบัตรได้ เนื่องจากคอร์สยังเรียนไม่จบ');
        return;
      }

      // ดึงชื่อ-สกุลจริงจาก user-information (fallback เป็น CID หากดึงไม่ได้)
      const currentUser = this.authService.getCurrentUser();
      const uid = record.userId || currentUser?.id;
      let userName = currentUser?.cid || 'User';
      try {
        const info: any = await firstValueFrom(
          this.http.get(`${environment.apiUrl}/user-information/user/${uid}`)
        );
        const fullName = [info?.titleName, info?.firstNameThai, info?.lastNameThai]
          .filter(Boolean).join(' ').trim();
        if (fullName) userName = fullName;
      } catch (e) {
        console.warn('⚠️ ดึงชื่อผู้ใช้ไม่สำเร็จ ใช้ CID แทน', e);
      }

      // Prepare certificate data
      const certificateData: CertificateData = {
        id: record.id,
        userName: userName,
        courseName: record.course.title,
        courseDescription: record.course.description,
        hoursStudied: record.hoursStudied,
        studyDate: record.studyDate,
        enrolledAt: record.enrolledAt,
        completedAt: record.completedAt,
        instructor: record.course.instructor,
        year: record.year,
        isVerified: record.isVerified,
        certificationBySEC: record.course?.certificationBySEC ?? (record as any).certificationBySEC ?? false,
        courseStartDate: record.course?.startDate ?? null,
        courseEndDate: record.course?.endDate ?? null,
        certificateNumber: this.certificateService.generateCertificateNumber(record.id, record.year)
      };

      // Generate and download certificate
      await this.certificateService.generateCertificate(certificateData);

      console.log('✅ Certificate downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading certificate:', error);
      alert('เกิดข้อผิดพลาดในการดาวน์โหลดใบประกาศนียบัตร กรุณาลองใหม่อีกครั้ง');
    }
  }
}
